import React, { useCallback, useEffect, useMemo, useState } from "react";

import { MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { Grid } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { handleSaveConvertValue } from "Util/converts";

import { useAppState } from "Context/StateContext";
import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import { useOrderContext } from "Hooks/useOrderContext";
import useYupValidation from "Hooks/useYupValidation";
import { Item } from "Models/item";
import { StorageBox } from "Models/storage.box";
import { Warehouse } from "Models/warehouse";
import { MapCell } from "Models/warehouse.mapcell";
import { canAddItemJobInLocation } from "Util/canAddItemJobInLocation";
import { ItemJobStatus, ItemProGearBelongsTo, JobStatus } from "Util/enums";
import { getMeasurement, getWeightMeasurement } from "Util/getMeasurement";
import { isEnableBOL } from "Util/isEnableBOL";
import { Input, Select } from "Components/Form";
import AutoSuggest from "Components/Form/AutoSuggest";
import Checkbox from "Components/Form/Checkbox";
import InputRange from "Components/Form/InputRange";
import RadioButton, { OptionProps } from "Components/Form/RadioGroup";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import EmptySection from "Components/Generic/SideView/EmptySection";
import SkeletonComponent from "Components/Generic/Skeleton";
import { resolveItemVolume } from "./helpers/resolveItemVolume";
import useStyles from "./styles";

const boxIcon = require("Assets/img/box-icon.svg");
const hardwareBoxIcon = require("Assets/img/hardwareBox-icon.svg");
const highRiskIcon = require("Assets/img/highRisk-icon.svg");
const meIcon = require("Assets/img/me-icon.svg");
const proGearIcon = require("Assets/img/proGear-icon.svg");

enum Location {
  NO_LOCATION = "no location",
  STORAGE_BOX = "storage box",
  WAREHOUSE = "warehouse",
}

export interface ItemJobLocation {
  mapCellId?: number;
  mapId?: number;
  sectionId?: number;
  storageBoxId?: number;
  warehouseId?: number;
}

interface DetailsProps {
  itemJobLocation?: ItemJobLocation;
  itemJobStatus?: string;
  setItemJobLocation: (location: ItemJobLocation) => void;
  setItemJobStatus: (status: string) => void;
  location?: string;
  setLocation?: (location: string) => void;
  warehouseList?: Warehouse[];
  mapCells?: MapCell[];
  isLoadingWarehouse?: boolean;
  isLoadingMapList?: boolean;
  storageBoxes?: StorageBox[];
  mustBeHardwareBox: boolean;
  orderStatus?: string;
  openOnlyComments?: boolean;
}

const Details = (props: DetailsProps) => {
  const {
    itemJobLocation,
    itemJobStatus,
    setItemJobLocation,
    setItemJobStatus,
    location,
    setLocation,
    warehouseList,
    mapCells,
    isLoadingWarehouse = false,
    isLoadingMapList = false,
    storageBoxes = [],
    mustBeHardwareBox,
    orderStatus,
    openOnlyComments,
  } = props;

  const {
    itemStatus,
    itemJob,
    setItemJob,
    jobModes,
    rooms,
    items,
    isLoadingItemList,
    itemsHardwareBox,
    itemsType,
    descriptiveSymbols,
    errors,
  } = useActualInventoryContext();

  const { order } = useOrderContext();

  const { currentUser } = useAppState();

  const checkEnableBOL = useCallback(() => isEnableBOL(order), [order?.tasks]);

  const classes = useStyles();

  const { fieldHasError, getErrorByField } = useYupValidation(errors || []);

  const { t: translate } = useTranslation();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const renderModes = () => {
    if (jobModes && jobModes.length > 0) {
      return jobModes?.map((jobMode) => (
        <MenuItem value={jobMode.name} key={jobMode.id}>
          {jobMode?.name}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderRooms = () => {
    if (rooms && rooms.length > 0) {
      return rooms?.map((room) => (
        <MenuItem value={room.id} key={room.id}>
          {room?.name}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderItemsTypes = () => {
    if (itemsType && itemsType.length > 0) {
      const badgeIsBox = (
        <Tooltip placement="top" title={translate("isBox")}>
          <img className={classes.nameTags} src={boxIcon} />
        </Tooltip>
      );

      const roomId = itemJob?.roomId;
      const filteredItemsType = roomId
        ? itemsType.filter((itemType) =>
            itemType?.rooms?.some((room) => room?.id === roomId)
          )
        : itemsType;

      const itemsTypesToRender = roomId && filteredItemsType.length > 0 ? filteredItemsType : itemsType;

      return itemsTypesToRender.map((itemType) => (
        <MenuItem value={itemType.id} key={itemType.id}>
          {itemType?.name} {itemType?.isBox && badgeIsBox}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderDescriptiveSymbols = () => {
    if (descriptiveSymbols && descriptiveSymbols.length > 0) {
      return descriptiveSymbols?.map((descriptiveSymbol) => (
        <MenuItem value={descriptiveSymbol.id} key={descriptiveSymbol.id}>
          {descriptiveSymbol?.name}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderItemStatus = () => {
    if (itemStatus && itemStatus.length > 0) {
      return itemStatus?.map((itemStatus) => {
        let disabled: boolean = false;

        if (
          itemStatus?.name?.toLowerCase() === ItemJobStatus.BOL.toLowerCase() &&
          !checkEnableBOL()
        ) {
          disabled = true;
        }

        if (itemJob?.status?.toLowerCase() !== ItemJobStatus.TRANSSHIPPED.toLowerCase()) {
          if (orderStatus?.toLowerCase() === JobStatus.TRANSSHIPPED.toLowerCase()) {
            if (
              [
                ItemJobStatus.NOT_SIGHTED.toLowerCase(),
                ItemJobStatus.TRANSSHIPPED.toLowerCase(),
              ].includes(itemStatus?.name?.toLowerCase())
            ) {
              disabled = true;
            }
          } else if (itemStatus?.name?.toLowerCase() === ItemJobStatus.TRANSSHIPPED.toLowerCase()) {
            disabled = true;
          }
        } else if (itemStatus?.name?.toLowerCase() !== ItemJobStatus.NOT_SIGHTED.toLowerCase()) {
          disabled = true;
        }

        return (
          <MenuItem disabled={disabled} value={itemStatus.name} key={itemStatus.id}>
            {itemStatus?.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const renderWarehouseForm = useCallback(() => {
    const sections = warehouseList
      ?.find((w) => w.id === itemJobLocation?.warehouseId)
      ?.sections?.filter((s) => s.allowsItemLoose)
      ?.sort((a, b) => a.name.localeCompare(b.name));

    const sectionId = itemJobLocation?.sectionId;

    const maps = sections
      ?.find((s) => s.id === sectionId)
      ?.maps?.sort((a, b) => a.name.localeCompare(b.name));

    const renderSectionOptions = () => {
      return sections?.map((section) => (
        <MenuItem value={section.id} key={`section-${section.id}`}>
          {section.name}
        </MenuItem>
      ));
    };

    const renderMapOptions = () => {
      return maps?.map((map) => (
        <MenuItem value={map.id} key={`map-${map.id}`}>
          {map.name}
        </MenuItem>
      ));
    };

    const renderWarehouseOptions = () => {
      return warehouseList?.map((w) => (
        <MenuItem value={w.id} key={`warehouse-${w.id}`}>
          {w.name}
        </MenuItem>
      ));
    };

    return (
      <SkeletonComponent isLoading={isLoadingWarehouse}>
        {warehouseList?.length === 0 ? (
          <EmptySection
            title={translate(
              "thisWarehouseDoesNotAllowStorageOfLooseItemsSelectAStorageBoxInstead"
            )}
          />
        ) : (
          <Grid container direction="row" spacing={2}>
            <Grid item xs={6} sm={3}>
              <Select
                required
                disabled={isLoadingWarehouse}
                value={itemJobLocation?.warehouseId}
                onChange={(event) => {
                  setItemJobLocation({
                    ...itemJobLocation,
                    warehouseId: Number(event.target.value),
                  });
                }}
                displayEmpty
                placeholder={translate("warehouse")}
                data-cy="warehouse"
              >
                {renderWarehouseOptions()}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Select
                required
                disabled={!itemJobLocation?.warehouseId || !sections?.length}
                value={itemJobLocation?.sectionId}
                onChange={(event) => {
                  if (!itemJobLocation?.warehouseId) return;
                  setItemJobLocation({
                    ...itemJobLocation,
                    sectionId: Number(event.target.value),
                  });
                }}
                displayEmpty
                placeholder={translate("section")}
                data-cy="section"
              >
                {renderSectionOptions()}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Select
                required
                disabled={!itemJobLocation?.sectionId}
                value={itemJobLocation?.mapId}
                onChange={(event) => {
                  setItemJobLocation({
                    ...itemJobLocation,
                    mapId: Number(event.target.value),
                    mapCellId: undefined,
                  } as ItemJobLocation);
                }}
                displayEmpty
                placeholder={translate("map")}
                data-cy="map"
              >
                {renderMapOptions()}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3}>
              <AutoSuggest
                required
                disabled={isLoadingMapList || !itemJobLocation?.mapId || !mapCells?.length}
                inputValue={mapCells?.find((cell) => cell.id === itemJobLocation?.mapCellId)?.name}
                placeholder={translate("location")}
                getOptionLabel={(cell: MapCell) => cell.name || ""}
                getOptionSelected={(option: MapCell, value: MapCell) => option.id === value.id}
                options={mapCells}
                renderOption={(props, cell) => {
                  const { itemsJobs, storageBoxes } = cell;

                  return (
                    <div {...props}>
                      {cell.name}
                      {!!itemsJobs?.length && (
                        <BadgeJobStatus
                          value={itemsJobs[0].description}
                          dataCy={`status-${itemsJobs[0].id}`}
                          className={classes.titleStatusBadge}
                        />
                      )}
                      {!!storageBoxes?.length && (
                        <BadgeJobStatus
                          value={storageBoxes[0].number}
                          dataCy={`status-${storageBoxes[0].id}`}
                          className={classes.titleStatusBadge}
                        />
                      )}
                    </div>
                  );
                }}
                onChange={(cell) => {
                  setItemJobLocation({
                    ...itemJobLocation,
                    mapCellId: cell?.id || undefined,
                    storageBoxId: undefined,
                  } as ItemJobLocation);
                }}
                error={fieldHasError("location")}
                errorMessage={getErrorByField("location")}
                data-cy="mapCell"
              />
            </Grid>
          </Grid>
        )}
      </SkeletonComponent>
    );
  }, [itemJobLocation, isLoadingMapList, warehouseList, mapCells, location, isLoadingWarehouse]);

  const getRadioButtonOptions = useCallback((): OptionProps[] => {
    const cantAddInLocation = !canAddItemJobInLocation(itemJobStatus);

    return [
      { label: translate("noLocation"), value: Location.NO_LOCATION },
      {
        label: translate("warehouse"),
        value: Location.WAREHOUSE,
        disabled: cantAddInLocation,
      },
      {
        label: translate("storageBox"),
        value: Location.STORAGE_BOX,
        disabled: cantAddInLocation,
      },
    ];
  }, [itemJobStatus]);

  const renderStorageBoxForm = () => {
    return (
      <Grid item xs={12} md={12} lg={12}>
        <Select
          placeholder={translate("select")}
          value={itemJobLocation?.storageBoxId}
          onChange={(event) => {
            setItemJobLocation({
              storageBoxId: Number(event.target.value),
              mapCellId: undefined,
              warehouseId: undefined,
              mapId: undefined,
              sectionId: undefined,
            } as ItemJobLocation);
          }}
        >
          {storageBoxes
            ?.sort((sb1, sb2) => sb1.number.localeCompare(sb2.number))
            .map((storageBox) => (
              <MenuItem key={`storage-box-${storageBox.id}`} value={storageBox.id}>
                {storageBox.number}
              </MenuItem>
            ))}
        </Select>
      </Grid>
    );
  };

  const renderLocationForm = () => {
    switch (location) {
      case "warehouse":
        return renderWarehouseForm();
      case "storage box":
        return renderStorageBoxForm();
      default:
        return <></>;
    }
  };

  const renderDescriptionOption = (props: any, item: Item) => {
    const nameTags = [];

    if (item?.isHardwareBox) {
      nameTags.push(
        <Tooltip placement="top" title={translate("hardwareBox")}>
          <img className={classes.nameTags} src={hardwareBoxIcon} />
        </Tooltip>
      );
    }

    if (item?.mandatoryME) {
      nameTags.push(
        <Tooltip placement="top" title={translate("me")}>
          <img className={classes.nameTags} src={meIcon} />
        </Tooltip>
      );
    }

    if (item?.highRisk) {
      nameTags.push(
        <Tooltip placement="top" title={translate("highRiskHighValue")}>
          <img className={classes.nameTags} src={highRiskIcon} />
        </Tooltip>
      );
    }

    if (item?.isProGear) {
      nameTags.push(
        <Tooltip placement="top" title={translate("proGear")}>
          <img className={classes.nameTags} src={proGearIcon} />
        </Tooltip>
      );
    }

    return (
      <div {...props}>
        {item?.name}
        {nameTags.map((tag) => tag)}
      </div>
    );
  };

  const disabledStatusField = () => {
    if (orderStatus?.toLowerCase() === JobStatus.TRANSSHIPPED.toLowerCase()) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (mustBeHardwareBox) {
      setItemJob((state) => ({ ...(state || {}), isHardwareBox: true }));
    }

    if (itemJob?.itemType?.isBox) {
      setItemJob((state) => ({ ...(state || {}), packedInto: "" }));
    } else {
      setItemJob((state) => ({ ...(state || {}), itemJobBoxContents: [] }));
    }
  }, [mustBeHardwareBox, itemJob?.itemType]);

  useEffect(() => {
    if (itemJobStatus && !canAddItemJobInLocation(itemJobStatus)) {
      if (setLocation) setLocation(Location.NO_LOCATION);
    }
  }, [itemJobStatus]);

  useEffect(() => {
    if (itemJobStatus?.toLowerCase() === ItemJobStatus.NOT_SIGHTED.toLowerCase()) {
      setLocation?.(Location.NO_LOCATION);
    }
  }, [itemJobStatus]);

  const isProGear = useMemo(() => !!itemJob?.isProGear, [itemJob]);

  useEffect(() => {
    if (itemJob?.item) {
      const itemTypeFound =
        itemsType && itemsType?.length > 0
          ? itemsType?.find((itemType) => itemType?.id === itemJob?.itemTypeId)
          : null;

      const convertedVolumeValue = handleSaveConvertValue(
        String(itemJob?.item?.volume),
        measurement?.code
      );

      if (
        Number(convertedVolumeValue.toFixed(measurement.decimalPlaces)) === 0 &&
        itemJob?.itemTypeId &&
        itemTypeFound?.volume &&
        itemTypeFound?.isBox
      ) {
        setItemJob((state) => ({
          ...(state || {}),
          volume: itemTypeFound?.volume,
        }));
      }
    }
  }, [itemJob?.description]);

  useEffect(() => {
    if (itemJob?.itemTypeId) {
      const itemTypeFound =
        itemsType && itemsType?.length > 0
          ? itemsType?.find((itemType) => itemType?.id === itemJob?.itemTypeId)
          : null;

      if (itemTypeFound?.isBox && itemTypeFound?.volume) {
        setItemJob((state) => ({
          ...(state || {}),
          volume: itemTypeFound?.volume,
        }));
      } else {
        setItemJob((state) => ({
          ...(state || {}),
          volume: state?.item?.volume || 0,
        }));
      }
    }
  }, [itemJob?.itemTypeId]);

  return (
    <Grid container className={classes.mainContainer} direction="row" spacing={2}>
      {!openOnlyComments && (
        <>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} sm={2}>
              <Input label={`${translate("item")} #`} value={itemJob?.number} disabled />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                label={translate("room")}
                value={itemJob?.roomId}
                required
                error={fieldHasError("roomId")}
                errorMessage={getErrorByField("roomId")}
                onChange={(event) => {
                  const roomId = Number(event.target.value);
                  const roomFound =
                    rooms && rooms?.length > 0 ? rooms?.find((room) => room?.id === roomId) : null;

                  setItemJob((state) => ({
                    ...(state || {}),
                    roomId: Number(event.target.value),
                    room: roomFound || undefined,
                  }));
                }}
                displayEmpty
                placeholder={translate("select")}
                data-cy="room"
              >
                {renderRooms()}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Select
                label={translate("packingType")}
                value={itemJob?.itemTypeId}
                onChange={(event) => {
                  const itemTypeId = Number(event.target.value);
                  const itemTypeFound =
                    itemsType && itemsType?.length > 0
                      ? itemsType?.find((itemType) => itemType?.id === itemTypeId)
                      : null;

                  setItemJob((state) => ({
                    ...(state || {}),
                    itemTypeId,
                    itemType: itemTypeFound || undefined,
                  }));
                }}
                displayEmpty
                placeholder={translate("select")}
                data-cy="packing-type"
              >
                {renderItemsTypes()}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                label={translate("status")}
                value={itemJobStatus}
                onChange={(event: SelectChangeEvent<any>) => {
                  setItemJobStatus(event.target.value);
                }}
                displayEmpty
                disabled={disabledStatusField()}
                placeholder={translate("select")}
                data-cy="status"
              >
                {renderItemStatus()}
              </Select>
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} sm={4}>
              <Select
                label={translate("methodOfPreparation")}
                value={itemJob?.descriptiveSymbolId}
                onChange={(event) => {
                  const symbolId = Number(event.target.value);
                  const symbolFound =
                    descriptiveSymbols && descriptiveSymbols?.length > 0
                      ? descriptiveSymbols?.find(
                          (descriptiveSymbol) => descriptiveSymbol?.id === symbolId
                        )
                      : null;

                  setItemJob((state) => ({
                    ...(state || {}),
                    descriptiveSymbolId: symbolId,
                    symbol: symbolFound || undefined,
                  }));
                }}
                displayEmpty
                placeholder={translate("select")}
                data-cy="descriptive-symbol"
              >
                {renderDescriptiveSymbols()}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Input
                label={translate("packer")}
                placeholder={translate("packer")}
                onChange={(event) =>
                  setItemJob((state) => ({ ...(state || {}), packer: event.target.value }))
                }
                value={itemJob?.packer}
                data-cy="packer"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SkeletonComponent
                classesName={classes.descriptionLoading}
                isLoading={isLoadingItemList}
              >
                <AutoSuggest
                  required
                  label={translate("itemDescription")}
                  inputValue={itemJob?.description}
                  placeholder={translate("searchOrAddAnItem")}
                  getOptionLabel={(item: Item) => (item && item?.name ? item?.name : "")}
                  getOptionSelected={(option: Item, value: Item) => option.id === value.id}
                  options={mustBeHardwareBox ? itemsHardwareBox : items}
                  onChange={(item: Item) => {
                    setItemJob((state) => ({
                      ...(state || {}),
                      description: item?.name,
                      itemId: item?.id,
                      item,
                      volume: resolveItemVolume({
                        item,
                        itemTypeId: state?.itemTypeId,
                        itemsType,
                      }),
                      weight: item?.weight,
                      isHardwareBox: item?.isHardwareBox,
                      isProGear: item?.isProGear,
                      isMeCustom: item?.mandatoryME,
                      isHighRisk: item?.highRisk,
                    }));
                  }}
                  onChangeInput={(value) => {
                    setItemJob((state) => ({
                      ...(state || {}),
                      description: value,
                      itemId: null,
                      item: undefined,
                    }));
                  }}
                  error={fieldHasError("description")}
                  errorMessage={getErrorByField("description")}
                  data-cy="item-description"
                  renderOption={renderDescriptionOption}
                />
              </SkeletonComponent>
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} sm={4}>
              <Select
                label={translate("mode")}
                value={itemJob?.mode}
                onChange={(event: SelectChangeEvent<any>) =>
                  setItemJob((state) => ({ ...(state || {}), mode: event.target.value }))
                }
                displayEmpty
                placeholder={translate("select")}
                data-cy="mode"
              >
                {renderModes()}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputRange
                label={translate("volume")}
                value={itemJob?.volume || 0}
                measurementInputRange={measurement}
                change={(value: number) =>
                  setItemJob((state) => ({ ...(state || {}), volume: value }))
                }
                minValue={0}
                data-cy="volume"
                containerClassName={classes.containerInputRange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputRange
                label={translate("weight")}
                measurementInputRange={measurementWeight}
                value={itemJob?.weight || 0}
                minValue={0}
                change={(value: number) =>
                  setItemJob((state) => ({ ...(state || {}), weight: value }))
                }
                data-cy="weight"
                containerClassName={classes.containerInputRange}
              />
            </Grid>
          </Grid>

          {itemJob?.itemType?.isBox && (
            <Grid item xs={12} sm={12}>
              <AutoSuggest
                label={translate("cartonContents")}
                placeholder={translate("pressEnterToAdd")}
                data-cy="box-content-tags"
                value={
                  itemJob && itemJob?.itemJobBoxContents
                    ? itemJob?.itemJobBoxContents?.map((content) => content.name)
                    : []
                }
                multiple
                freeSolo
                getOptionLabel={(option: string) => option}
                options={items?.map((item) => item.name)}
                onChange={(tags: string[]) => {
                  const boxContentsSelected = tags
                    ? tags?.map((tag) => {
                        const item = items?.find(
                          (i) => i?.name?.toLowerCase()?.trim() === tag?.toLowerCase()?.trim()
                        );

                        if (item) {
                          return {
                            name: item.name,
                            itemId: item.id,
                          };
                        }

                        return {
                          name: tag,
                          itemId: null,
                        };
                      })
                    : [];

                  setItemJob((state) => ({
                    ...(state || {}),
                    itemJobBoxContents: boxContentsSelected,
                  }));
                }}
                error={fieldHasError("itemJobBoxContents")}
                errorMessage={getErrorByField("itemJobBoxContents")}
              />
            </Grid>
          )}

          <div className={classes.checkboxGroup}>
            <div className={classes.checkboxItem}>
              <Checkbox
                disabled={!!itemJob?.item}
                checked={!!itemJob?.isMeCustom}
                label={translate("itemIsAME")}
                onChange={(event) =>
                  setItemJob((state) => ({
                    ...(state || {}),
                    isMeCustom: event.currentTarget.checked,
                  }))
                }
              />
              {!!itemJob?.isMeCustom || !!itemJob?.item?.mandatoryME ? (
                <div className={classnames(classes.mt20, classes.dflex)}>
                  <Input
                    label={translate("make")}
                    placeholder={translate("make")}
                    error={fieldHasError("make")}
                    errorMessage={getErrorByField("make")}
                    required
                    value={itemJob?.make}
                    onChange={(event) =>
                      setItemJob((state) => ({ ...(state || {}), make: event.target.value }))
                    }
                  />
                  <Input
                    label={translate("model")}
                    placeholder={translate("model")}
                    error={fieldHasError("model")}
                    errorMessage={getErrorByField("model")}
                    required
                    value={itemJob?.model}
                    onChange={(event) =>
                      setItemJob((state) => ({ ...(state || {}), model: event.target.value }))
                    }
                  />
                  <Input
                    label={translate("serialNumber")}
                    placeholder={translate("serialNumber")}
                    error={fieldHasError("serialNumber")}
                    errorMessage={getErrorByField("serialNumber")}
                    required
                    value={itemJob?.serialNumber}
                    onChange={(event) =>
                      setItemJob((state) => ({
                        ...(state || {}),
                        serialNumber: event.target.value,
                      }))
                    }
                  />
                </div>
              ) : null}
            </div>
            {currentUser?.company?.isMilitary && (
              <div className={classes.checkboxItem}>
                <Checkbox
                  disabled={!!itemJob?.item}
                  checked={!!itemJob?.isProGear}
                  label={translate("itemIsProGear")}
                  onChange={(event) =>
                    setItemJob((state) => ({
                      ...(state || {}),
                      isProGear: event.currentTarget.checked,
                    }))
                  }
                />
                {isProGear && (
                  <div className={classes.mt20}>
                    <RadioButton
                      value={itemJob?.proGearBelongsTo}
                      label={translate("whoDoesThisItemBelongTo")}
                      options={[
                        { label: translate("member"), value: ItemProGearBelongsTo.MEMBER },
                        { label: translate("spouse"), value: ItemProGearBelongsTo.SPOUSE },
                      ]}
                      onChange={(event) =>
                        setItemJob((state) => ({
                          ...(state || {}),
                          proGearBelongsTo: event.target.value,
                        }))
                      }
                      error={fieldHasError("proGearBelongsTo")}
                      errorMessage={getErrorByField("proGearBelongsTo")}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={classes.checkboxItem}>
              <Checkbox
                disabled={!!itemJob?.item || mustBeHardwareBox}
                label={translate("itemIsAHardwareBox")}
                checked={!!itemJob?.isHardwareBox}
                onChange={(event) =>
                  setItemJob((state) => ({
                    ...(state || {}),
                    isHardwareBox: event.currentTarget.checked,
                  }))
                }
              />
            </div>
            <div className={classes.checkboxItem}>
              <Checkbox
                disabled={!!itemJob?.item}
                checked={!!itemJob?.isHighRisk}
                label={translate("itemIsHighRiskOrHighValue")}
                onChange={(event) =>
                  setItemJob((state) => ({
                    ...(state || {}),
                    isHighRisk: event.currentTarget.checked,
                  }))
                }
              />
            </div>
          </div>

          <Grid item xs={12} sm={12}>
            <RadioButton
              label={translate("location")}
              onChange={(event) => {
                if (setLocation) setLocation(event.target.value);
              }}
              options={getRadioButtonOptions()}
              value={location}
            />
            {renderLocationForm()}
          </Grid>
        </>
      )}
      <Grid
        item
        xs={12}
        sm={12}
        style={{
          paddingLeft: openOnlyComments ? "32px" : undefined,
          paddingRight: openOnlyComments ? "32px" : undefined,
        }}
      >
        <Input
          label={translate("comments")}
          placeholder={translate("typeYourCommentHere")}
          disabled={openOnlyComments}
          onChange={(event) =>
            setItemJob((state) => ({ ...(state || {}), comments: event.target.value }))
          }
          value={itemJob?.comments}
          multiline
          data-cy="comments"
        />
      </Grid>
    </Grid>
  );
};

export default Details;
